import { For, Show, createMemo, createSignal } from 'solid-js';
import { Button } from '~shared/ui/Button';
import { Card } from '~shared/ui/Card';
import { Chip } from '~shared/ui/Chip';
import { Label } from '~shared/ui/Label';
import { Textarea } from '~shared/ui/Textarea';
import { findOverlaps, lookup, resultPriority } from '~/lib/history';
import { parseNames } from '~/lib/parse';
import { snapshotState } from '~/stores/snapshotStore';
import { ResultGroup } from './member/ResultGroup';

const PLACEHOLDER = 'ada lovelace (summer), grace hopper, turing (winter)';

export function Lookup() {
  const [text, setText] = createSignal('');
  const [submitted, setSubmitted] = createSignal('');

  const tokens = createMemo(() => parseNames(submitted()));
  const results = createMemo(() => {
    const index = snapshotState.index;
    if (!index) return [];
    return tokens().map((t) => lookup(index, t));
  });
  const overlaps = createMemo(() => findOverlaps(results()));
  const sorted = createMemo(() =>
    results()
      .slice()
      .sort((a, b) => resultPriority(a) - resultPriority(b)),
  );

  return (
    <>
      <Card>
        <section class="flex flex-col gap-3" aria-labelledby="lookup-heading">
          <h2 id="lookup-heading" class="text-base font-semibold">
            Look up members
          </h2>
          <div class="flex flex-col gap-1">
            <Label for="names">
              Names or emails — separated by commas or new lines. Text in
              parentheses is ignored.
            </Label>
            <Textarea
              id="names"
              rows={4}
              placeholder={PLACEHOLDER}
              value={text()}
              onInput={(e) => setText(e.currentTarget.value)}
            />
            <p class="text-muted text-xs">
              Because commas separate people, write names as “Grace Hopper”, not
              “Hopper, Grace”.
            </p>
          </div>
          <div class="flex items-center gap-3">
            <Button
              kind="primary"
              onClick={() => setSubmitted(text())}
              disabled={!text().trim()}
            >
              Look up
            </Button>
            <Show when={submitted()}>
              <Button
                onClick={() => {
                  setText('');
                  setSubmitted('');
                }}
              >
                Clear
              </Button>
            </Show>
            <Show when={tokens().length}>
              <span class="text-muted text-xs">
                {tokens().length} name(s) parsed
              </span>
            </Show>
          </div>
        </section>
      </Card>

      <Show when={overlaps().length}>
        <Card>
          <details class="flex flex-col gap-2">
            <summary class="cursor-pointer text-base font-semibold">
              Shared teams &amp; clinics ({overlaps().length})
            </summary>
            <ul class="flex flex-col gap-1.5 pt-2">
              <For each={overlaps()}>
                {(o) => (
                  <li class="flex flex-wrap items-baseline gap-2 text-xs">
                    <Chip tone={o.kind === 'team' ? 'tier-1' : 'tier-2'}>
                      {o.kind === 'team' ? 'Team' : 'Clinic'}
                    </Chip>
                    <span class="font-medium">{o.label}</span>
                    <span class="text-muted">{o.memberNames.join(', ')}</span>
                  </li>
                )}
              </For>
            </ul>
          </details>
        </Card>
      </Show>

      <Show when={results().length}>
        <div class="flex flex-col gap-5">
          <For each={sorted()}>{(r) => <ResultGroup result={r} />}</For>
        </div>
      </Show>
    </>
  );
}
